export interface InlineSelectOption<T = string> {
  label: string;
  value: T;
}

interface InlineSelectInputProps<T = string> {
  options: InlineSelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T | null) => void;
  name: string;
  label?: string;
  required?: boolean;
  className?: string;
  hideLabel?: boolean;
  /** Label for the "all" badge. Default: "Tous" */
  allLabel?: string;
  /** Show the "all" badge. Default: true */
  showAllOption?: boolean;
}

/**
 * Composant de sélection inline avec style badge (similaire à MultiSelectInput)
 * Utilisé pour des choix exclusifs (comportement radio)
 */
export const FilterInlineSelectInput = <T extends string = string>({
  options,
  value,
  onChange,
  name,
  label,
  required = false,
  className = '',
  hideLabel = false,
  allLabel = 'Tous',
  showAllOption = true,
}: InlineSelectInputProps<T>) => {
  const isAllSelected = value == null;

  return (
    <div className={className}>
      {label && !hideLabel && (
        <h4 className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </h4>
      )}

      <div className="tw-flex tw-gap-1 tw-flex-wrap">
        {/* Badge "Tous" */}
        {showAllOption && (
          <label
            className={`
              tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1
              tw-border-2 tw-rounded-md tw-cursor-pointer
              tw-transition-all tw-select-none tw-whitespace-nowrap
              ${isAllSelected
                ? 'tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7] tw-border-white tw-text-white tw-shadow-md'
                : 'tw-bg-gray-100 tw-border-gray-300 tw-text-gray-700 hover:tw-border-gray-400'
              }
            `}
          >
            <input
              type="radio"
              name={name}
              value=""
              checked={isAllSelected}
              onChange={() => onChange(null)}
              className="tw-sr-only"
            />
            <span className="tw-font-medium tw-text-xs">{allLabel}</span>
          </label>
        )}

        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={String(option.value)}
              className={`
                tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1
                tw-border-2 tw-rounded-md tw-cursor-pointer
                tw-transition-all tw-select-none tw-whitespace-nowrap
                ${isSelected
                  ? 'tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7] tw-border-white tw-text-white tw-shadow-md'
                  : 'tw-bg-gray-100 tw-border-gray-300 tw-text-gray-700 hover:tw-border-gray-400'
                }
              `}
            >
              <input
                type="radio"
                name={name}
                value={String(option.value)}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="tw-sr-only"
              />
              <span className="tw-font-medium tw-text-xs">{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
